import { Component, OnInit } from '@angular/core';
import { MdDialog, MdDialogRef } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { SelectItem } from 'primeng/primeng';
import { FormErrors, isValidMailFormat } from 'app/shared/validators/generic-validators';

import { Group } from 'app/admin/groups/group.model';
import { User } from 'app/shared/models/user.model';
import { GroupService } from 'app/admin/groups/group.service';

@Component({
    selector: 'add-group',
    templateUrl: './add-group.component.html',
    styleUrls: ['./add-group.component.css']
})
/**
 * Component to add a new group.
 */
export class AddGroupComponent implements OnInit {
    action: string;
    selectedGroup: Group;
    groupForm: FormGroup;

    arrComplianceOfficer: any[] = [];
    arrExternalComplianceOfficer: any[] = [];
    arrManager: any[] = [];
    arrMemberUser: SelectItem[];
    selectedMemberUsers: any[] = [];

    groupId: string;
    actionType: string;

    formErrors = {
        'name': '',
        'complianceOfficer': '',
        'manager': '',
    };

    validationMessages = {
        'name': {
            'required': 'Required field value.',
        },
        'complianceOfficer': {
            'required': 'Required field value.',
        },
        'manager': {
            'required': 'Required field value.',
        },
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private fb: FormBuilder,
        private groupService: GroupService,
        private trackingService: TrackingService,
        private snackBarService: SnackBarService,
    ) { }

    ngOnInit() {
        this.buildForm();
        this.fillUpAllUserDropDownValues([]);
        this.updateGroupForm();
        this.actionType = this.action === 'Add' && 'New' || 'Edit';
    }

    fillUpAllUserDropDownValues(dropDownValues: any) {
        const fetchedmemberUsers = [];
        const formattedUsers = [];
        const formattedMemberUsers = [];
        this.groupService.getUsersList()
            .subscribe(response => {
                response.forEach((argFetchedUserRecord, index) => {
                    if (argFetchedUserRecord.name) {
                        formattedMemberUsers.push(
                            {
                                'label': argFetchedUserRecord.name,
                                'value': argFetchedUserRecord.loginId,
                            });
                        formattedUsers.push(
                            {
                                'id': argFetchedUserRecord.loginId,
                                'value': argFetchedUserRecord.name,
                            });
                    }
                });
                this.arrComplianceOfficer = this.arrExternalComplianceOfficer = this.arrManager = formattedUsers;
                this.arrMemberUser = formattedMemberUsers;
            });
    }

    buildForm() {
        this.groupForm = this.fb.group({
            name: ['', Validators.required],
            complianceOfficer: ['', Validators.required],
            externalComplianceOfficer: [],
            manager: ['', Validators.required],
            memberUsers: [],
        });
        this.groupForm.valueChanges.subscribe(data => this.onValueChanged(data));
        this.onValueChanged();
    }

    onValueChanged(data?: any) {
        if (!this.groupForm) {
            return;
        }
        const form = this.groupForm;
        for (const field in this.formErrors) {
            // clear previous error message (if any)
            this.formErrors[field] = '';
            const control = form.get(field);
            if (control && control.touched && !control.valid) {
                const messages = this.validationMessages[field];
                for (const key in control.errors) {
                    this.formErrors[field] += messages[key] + ' ';
                }
            }
        }
    }

    onSubmit({ value, valid }) {
        const userGroup = new Group();
        // Group Id only if action is Edit
        if (this.groupId) {
            userGroup.id = this.groupId;
        }
        // Get Group Name
        userGroup.name = value.name;
        // Get Compliance Officer Value
        if(value.complianceOfficer)
        {
            userGroup.complianceOfficer = new User();
            userGroup.complianceOfficer.loginId = value.complianceOfficer;
        }
        // Get External Compliance Officer Value
        if(value.externalComplianceOfficer)
        {
            userGroup.externalComplianceOfficer = new User();
            userGroup.externalComplianceOfficer.loginId = value.externalComplianceOfficer;
        }
        // Get Manager Value
        if(value.manager)
        {
            userGroup.manager = new User();
            userGroup.manager.loginId = value.manager;
        }
        // Get Selected Member Values
        userGroup.memberUsers = new Array<User>();
        if (value.memberUsers) {
            value.memberUsers.forEach((memberUser, index) => {
                const user = new User();
                user.loginId = memberUser;
                userGroup.memberUsers.push(user);
            });
        }
        // Add or Edit based on action.
        if (this.action.toLowerCase() === 'add') {
            this.groupService.addGroup(userGroup)
                .subscribe(response => {
                    if (response.flagResult) {
                        this.dialogRef.close(response.arrResult);
                    }
                    this.trackingService
                        .emitEvent(
                        TrackingConstants.GROUP_MANAGEMENT,
                        TrackingConstants.SAVE_GROUP,
                        TrackingConstants.SUCCESS, 1);
                    this.callSnackBarService(response, this.action);
                }, err => {
                    this.trackingService
                        .emitEvent(
                        TrackingConstants.GROUP_MANAGEMENT,
                        TrackingConstants.SAVE_GROUP,
                        TrackingConstants.FAILURE, 0);
                    this.callSnackBarService(err, this.action);
                });
        } else {
            this.groupService.updateGroup(userGroup)
                .subscribe(response => {
                    if (response.flagResult) {
                        this.dialogRef.close(response.arrResult);
                    }
                    this.trackingService
                        .emitEvent(
                        TrackingConstants.GROUP_MANAGEMENT,
                        TrackingConstants.SAVE_GROUP,
                        TrackingConstants.SUCCESS, 1);
                    this.callSnackBarService(response, this.action);
                }, err => {
                    this.trackingService
                        .emitEvent(
                        TrackingConstants.GROUP_MANAGEMENT,
                        TrackingConstants.SAVE_GROUP,
                        TrackingConstants.FAILURE, 0);
                    this.callSnackBarService(err, this.action);
                });
            this.closeDialog();
        }
    }

    callSnackBarService(response: any, operation: string) {
        this.snackBarService
            .showStatus(response,
            operation === 'Add'
            && MessageService.ADD_GROUP_SUCCESS || MessageService.UPDATE_GROUP_SUCCESS,
            MessageService.GROUP_FAILURE);
    }

    closeDialog(): void {
        this.groupForm.reset();
        this.dialogRef.close();
    }

    updateGroupForm(): void {
        if (this.selectedGroup) {
            this.groupId = this.selectedGroup.id;
            let groupMemberUsers = [];
            this.selectedMemberUsers = [];
            groupMemberUsers = this.selectedGroup.memberUsers;
            groupMemberUsers.forEach((argMemberRecord, index) => {
                if (argMemberRecord.loginId) {
                    this.selectedMemberUsers.push(argMemberRecord.loginId);
                }
            });
            this.groupForm.patchValue({
                name: this.selectedGroup.name,

                complianceOfficer:
                (this.selectedGroup.complianceOfficer)
                ? this.selectedGroup.complianceOfficer.loginId
                : '',

                manager:
                (this.selectedGroup.manager)
                ? this.selectedGroup.manager.loginId
                : '',

                externalComplianceOfficer: 
                (this.selectedGroup.externalComplianceOfficer)
                ? this.selectedGroup.externalComplianceOfficer.loginId
                : '',
                
                memberUsers: this.selectedMemberUsers
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/admin/groups/addgroup/add-group.component.ts